/**
 * Community Image Carousel
 * Displays community cover images and videos with dot indicators
 */

import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { Pause, Play } from "lucide-react"
import * as React from "react"
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { cn } from "@/lib"
import type { CarouselMediaItem } from "../types/community-detail"

interface CommunityImageCarouselProps {
  media: CarouselMediaItem[]
  leadingElement?: React.ReactNode
}

// Convert regular URL to embed URL for YouTube/Vimeo
function getEmbedUrl(url: string): string {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
  if (ytMatch)
    return `https://www.youtube.com/embed/${ytMatch[1]}?controls=1&rel=0&modestbranding=1&iv_load_policy=3&playsinline=1`

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch)
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?badge=0&byline=0&portrait=0&title=0&controls=0`

  return url
}

export function CommunityImageCarousel({ media, leadingElement }: CommunityImageCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)
  const [playingIndex, setPlayingIndex] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
      // Stop video when navigating away from video slide
      setPlayingIndex(null)
    })
  }, [api])

  const placeholderCount = media.length > 0 ? media.length : 4

  const handlePlayVideo = (index: number) => {
    setPlayingIndex(index)
  }

  const handlePauseVideo = () => {
    setPlayingIndex(null)
  }

  return (
    <div className="w-full relative">
      <Carousel
        setApi={setApi}
        plugins={[WheelGesturesPlugin()]}
        className="w-full max-w-360 mx-auto"
      >
        {/* Leading Element - positioned at top left inside carousel */}
        {leadingElement && <div className="absolute top-6.25 left-6.25 z-30">{leadingElement}</div>}
        <CarouselPrevious className="absolute left-5 md:left-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 bg-white/5 backdrop-blur-xs" />
        <CarouselNext className="absolute right-5 md:right-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 bg-white/5 backdrop-blur-xs" />
        <CarouselContent>
          {media.length > 0
            ? media.map((item, index) => (
                <CarouselItem key={index}>
                  <div className="max-h-[600px] w-full overflow-hidden relative bg-black min-[1445px]:rounded-[30px]">
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={`Community image ${index + 1}`}
                        className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                      />
                    ) : (
                      <div className="relative w-full h-[600px] min-[1445px]:rounded-[30px]">
                        {playingIndex === index ? (
                          <iframe
                            src={`${getEmbedUrl(item.videoUrl)}&autoplay=1`}
                            className="absolute inset-0 w-full h-full min-[1445px]:rounded-[30px]"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          />
                        ) : (
                          <>
                            <img
                              src={item.thumbnailUrl || ""}
                              alt={item.title || "Video thumbnail"}
                              className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                            />
                            <button
                              onClick={() => handlePlayVideo(index)}
                              className="absolute inset-0 flex items-center justify-center "
                              aria-label="Play video"
                            >
                              <Play className="h-10 w-10 text-white" fill="white" />
                            </button>
                          </>
                        )}
                        {/* Pause button when playing */}
                        {playingIndex === index && (
                          <button
                            onClick={handlePauseVideo}
                            className="absolute  inset-0  p-3 rounded-full"
                            aria-label="Pause video"
                          >
                            <Pause className="h-8 w-8 text-white" fill="white" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))
            : Array.from({ length: placeholderCount }).map((_, index) => (
                <CarouselItem key={index}>
                  <div className="max-h-[600px] w-full overflow-hidden bg-card" />
                </CarouselItem>
              ))}
        </CarouselContent>
      </Carousel>

      {/* Dot Indicators */}
      <div className="mb-[30px] flex items-center justify-center gap-2 absolute inset-x-0 bottom-0">
        {Array.from({ length: count || placeholderCount }).map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={cn(
              "h-1 w-8 rounded-none transition-all",
              index === current ? "bg-[#F2F0EB]" : "bg-[#F2F0EB] opacity-20",
            )}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
